import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import Cta from '@gojob/ui-bits/Cta';
import Flex, { FlexProps } from '@gojob/ui-bits/Flex';
import useTheme from '@gojob/ui-bits/styles/useTheme';
import TranslationProvider from '@gojob/ui-bits/TranslationProvider';

import useTranslation from '../../translations/useTranslation';
import Link from '../Link';
import Keyword from './Keyword';
import PlaceAutocomplete from './PlaceAutocomplete';

export interface JobSearchFormProps extends FlexProps<'form'> {
  buttonId?: string;
  inputLabel?: string;
  buttonLabel?: string;
  href?: string;
}

const keywordExample = 'Cariste';
const placeExample = 'Marseille';

const TIME_BEFORE_TYPING = 1000;
const TIME_BETWEEN_TYPING = 250;
const TIME_BEFORE_CLEARING = 5000;

const JobSearchRedirection: FunctionComponent<JobSearchFormProps> = (props) => {
  const { inputLabel, buttonLabel, buttonId, href, ...other } = props;
  const { spacing } = useTheme();
  const t = useTranslation();

  const { keyword, place } = useTypingEffect(keywordExample, placeExample);

  return (
    <TranslationProvider translationFunction={t}>
      <Flex as={Link} href={href} flexDirection="column" alignItems="stretch" {...other}>
        <Keyword keyword={keyword} inputLabel={inputLabel} />
        <PlaceAutocomplete place={place} />

        <Cta id={buttonId} variant="primary" type="button" css={{ margin: spacing(8, 0, 0) }}>
          {buttonLabel ? buttonLabel : t('find_my_job')}
        </Cta>
      </Flex>
    </TranslationProvider>
  );
};

export default JobSearchRedirection;

const useTypingEffect = (firstWord: string, secondWord: string) => {
  const [typingStart, setTypingStart] = useState(false);
  const [typingEnd, setTypingEnd] = useState(false);

  const [firstWordStep, setFirstWordStep] = useState(0);
  const [secondWordStep, setSecondWordStep] = useState(0);

  const [firstWordTypedValue, setFirstWordTypedValue] = useState<string | undefined>('');
  const [secondWordTypedValue, setSecondWordTypedValue] = useState<string | undefined>('');

  const reset = useCallback(() => {
    setTypingStart(false);
    setTypingEnd(false);
    setFirstWordStep(0);
    setSecondWordStep(0);
    setFirstWordTypedValue('');
    setSecondWordTypedValue('');
  }, []);

  useEffect(() => {
    if (!typingStart) {
      setTimeout(() => setTypingStart(true), TIME_BEFORE_TYPING);
    }
  }, [typingStart]);

  useEffect(() => {
    if (typingStart) {
      const interval = setInterval(() => {
        if (isWordCompleted(firstWord, firstWordStep)) {
          const keywordValue = firstWord.substring(0, firstWordStep);
          setFirstWordTypedValue(keywordValue);
          setFirstWordStep((step) => step + 1);
        } else if (isWordCompleted(secondWord, secondWordStep)) {
          const placeValue = secondWord.substring(0, secondWordStep);
          setSecondWordTypedValue(placeValue);
          setSecondWordStep((step) => step + 1);
        } else {
          setTypingEnd(true);
        }
      }, TIME_BETWEEN_TYPING);

      return () => clearInterval(interval);
    }
  }, [typingStart, firstWordStep, secondWordStep]);

  useEffect(() => {
    if (typingEnd) {
      const timeout = setTimeout(reset, TIME_BEFORE_CLEARING);
      return () => clearTimeout(timeout);
    }
  }, [typingEnd]);

  return { keyword: firstWordTypedValue, place: secondWordTypedValue };
};

const isWordCompleted = (word: string, step: number) => step <= word.length;
